import * as React from 'react';
import { codes } from 'currency-codes';
import {
  Select,
  TextArea,
  NumberField,
  DateTime,
  Text,
  Checkbox,
  CountrySelect,
} from '../components/forms';
import { Link } from 'react-router-dom';
import Price from '../components/forms/Price';
import Page from '../components/Page';
import Section from '../components/Section';
import { ButtonStyle } from '../components/Button';
import { ActionBar, ActionBarStyle } from '../components/ActionBar';
import { Contract } from '../contract';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { ProfileContext, ProfileManager } from '../profile';
import { useRequestAsync, getHeaders } from '../api/client';
import { CompanySelect } from '../components/forms/CompaniesSelect';

type Props = {
  contract: Contract;
};

const contractTypes = () => [
  { label: 'Sale Percentage (%)', value: 'sale_percentage' },
  { label: 'Sale Fee/Comission', value: 'sale_fee' },
  { label: 'Monthly Subscription', value: 'monthly_fee' },
];

const update = (context: ProfileManager) =>
  useRequestAsync((data: Contract) => ({
    url: data.id ? `/store/contracts/${data.id}` : '/store/contracts/new',
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

const setValue = (obj: Contract, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

const setBillingInfoVvalue = (obj: Contract, onChange: Function) => (
  field: string,
  newValue: String,
) =>
  onChange(
    Object.assign({}, obj, {
      billing_info: {
        ...obj.billing_info,
        [field]: newValue,
      },
    }),
  );

export default function EditContract(props: Props) {
  const context = React.useContext(ProfileContext);
  const [contract, setContract] = React.useState(props.contract);

  const [state, submit] = update(context);

  const handleFormSubmit = () =>
    submit({
      ...contract,
      ...contract,
    });

  return (
    <Page
      title={contract.company_name || 'A new contract'}
      subtitle="Edit contract"
    >
      <div className="flex justify-center ml-auto mr-auto">
        <Link
          to="/contracts"
          className="f5 link br-pill bg-near-white hover-bg-black-10 ph4 pv3 mh2 b black"
        >
          ← Back to Contracts
        </Link>
      </div>
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && state.data && (
        <SuccessFlash text="Contract has been saved" />
      )}
      <form onSubmit={handleFormSubmit} id="contract">
        <Section title="Basic details">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <CompanySelect
                label="Company"
                field={['company_id']}
                id="company_id"
                defaultValue={contract.company_id}
                onValueChange={setValue(contract, setContract)}
              />
              <TextArea
                label="Additional Notes"
                field={['notes']}
                id="notes"
                defaultValue={contract.notes}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <NumberField
                label="No. of trial days"
                field={['trial_days']}
                id="trial_days"
                defaultValue={contract.trial_days}
                onValueChange={setValue(contract, setContract)}
              />
              <NumberField
                label="Limit of products"
                field={['product_limit']}
                id="product_limit"
                defaultValue={contract.product_limit}
                onValueChange={setValue(contract, setContract)}
              />
              <Checkbox
                label="Active?"
                field={['is_active']}
                id="is_active"
                defaultValue={contract.is_active}
                onValueChange={setValue(contract, setContract)}
              />
              <DateTime
                label="Active From"
                field={['started_at']}
                id="started_at"
                defaultValue={contract.started_at}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <Section title="Commission">
          <div className="flex">
            <div className="flex-auto w-50 pr2">
              <Select
                label="Contract Type"
                field={['type']}
                id="type"
                options={contractTypes()}
                defaultValue={contract.type}
                onValueChange={setValue(contract, setContract)}
              />
              <NumberField
                label="Percentage (0-100 %)"
                field={['percentage']}
                id="percentage"
                defaultValue={contract.percentage}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
            <div className="flex-auto w-50 pl2">
              <Price
                label="Comission Price (With decimal point i.e. 8.00)"
                field={['amount']}
                id="amount"
                defaultValue={contract.amount}
                onValueChange={setValue(contract, setContract)}
              />
              <Select
                label="Currency"
                field={['amount_currency']}
                id="amount_currency"
                options={codes().map((value: string) => ({
                  value,
                  label: value,
                }))}
                defaultValue={contract.amount_currency}
                onValueChange={setValue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <Section title="Billing Information">
          <div className="flex flex-column flex-row-ns">
            <div className="w-100 w-50-ns pr2-ns">
              <Text
                label="Address City"
                field={['address_city']}
                id="address_city"
                defaultValue={contract.billing_info.address_city}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <CountrySelect
                label="Address Country"
                field={['address_country']}
                id="address_country"
                defaultValue={contract.billing_info.address_country}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Address County"
                field={['address_county']}
                id="address_county"
                defaultValue={contract.billing_info.address_county}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Address Line 1"
                field={['address_line_1']}
                id="address_line_1"
                defaultValue={contract.billing_info.address_line_1}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Address Line 2"
                field={['address_line_2']}
                id="address_line_2"
                defaultValue={contract.billing_info.address_line_2}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Address Postal Code"
                field={['address_postal_code']}
                id="address_postal_code"
                defaultValue={contract.billing_info.address_postal_code}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
            <div className="w-100 w-50-ns pl2-ns">
              <Text
                label="Account Holder"
                field={['account_holder']}
                id="account_holder"
                defaultValue={contract.billing_info.account_holder}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Account Number"
                field={['account_number']}
                id="account_number"
                defaultValue={contract.billing_info.account_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Bank Address"
                field={['bank_address']}
                id="bank_address"
                defaultValue={contract.billing_info.bank_address}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Bank Name"
                field={['bank_name']}
                id="bank_name"
                defaultValue={contract.billing_info.bank_name}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Routing/ABA/Sort Code"
                field={['routing_number']}
                id="routing_number"
                defaultValue={contract.billing_info.routing_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
              <Text
                label="Swift Number"
                field={['swift_number']}
                id="swift_number"
                defaultValue={contract.billing_info.swift_number}
                onValueChange={setBillingInfoVvalue(contract, setContract)}
              />
            </div>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Save',
              handler: handleFormSubmit,
            },
          ]}
        />
      </form>
    </Page>
  );
}
